"use client";

import React from "react";
import { useTranslations } from "next-intl";
import Breadcrumb from "@/components/Common/Breadcrumb";
import { getImagePath } from "@/lib/utils";
import { trackCtaClicked } from "@/lib/analytics";
import Benefits from "@/components/Benefits";
import Testimonials from "@/components/Testimonials";
import HowItWork from "@/components/HowItWorks";
import TopFilmsSingleSection from "@/components/TopFilms";
import { useLocale } from "next-intl";
import { formatNumberByLocale } from "@/utils/numberFormatter";

export default function Page() {
  const t = useTranslations("benefitsForCustomers");

  const items = t.raw("items"); // full array
  const locale = useLocale();

  return (
    <section className="section-padding bg-white py-24">
      {/* Breadcrumb */}
      <Breadcrumb
        pageName={t("title")}
        description={t("description")}
        bgImages={[getImagePath(t("breadcrumbBg"))]}
        bgIndex={0}
        // buttons={[
        //   {
        //     label: t("buttons.joinNow"),
        //     href: "/join-partner",
        //     props: {
        //       variant: "default",
        //       className:
        //         "ml-2 rounded-tl-xl rounded-br-xl bg-white text-primary hover:bg-primary hover:text-white",
        //       href: "/join-partner",
        //     },
        //   },
        // ]}
      />

      {/* Full Information Banner */}
      <div className="container mx-auto md:px-6 mt-5">
        <div className="py-4 md:py-5 flex flex-col md:flex-row items-start justify-between gap-6">
          <div className="leading-relaxed">
            <h2 className="text-primary text-3xl">
              {t("fullInfo")}
            </h2>
            <p>
              {t("fullInfoDesc")}
            </p>
          </div>
          <button
            type="button"
            onClick={() => {
              trackCtaClicked({ cta: "download_pdf", page: "benefits_customer" });
              const url = t("fullInfoLink");
              window.open(url, "_blank");
              const a = document.createElement("a");
              a.href = url;
              a.download = "";
              document.body.appendChild(a);
              a.click();
              document.body.removeChild(a);
            }}
            className="inline-flex items-center gap-3 bg-orange-500 hover:bg-orange-600 text-white font-bold text-sm md:text-lg uppercase tracking-wider px-8 md:px-10 py-4 md:py-5 rounded-tl-2xl rounded-br-2xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 whitespace-nowrap"
          >
            {t("downloadPdf")}
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 16l-5-5h3V4h4v7h3l-5 5zm-7 2h14v2H5v-2z" />
            </svg>
          </button>
        </div>
      </div>

      {/* Section Items */}
      <div className="container grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-20 items-center justify-center mt-5">
        <div>
          {items.slice(0, 2).map((item: any) => (
            <div key={item.id} className="flex items-start mb-10 gap-5">
              <h1 className="numbering text-primary">
                {formatNumberByLocale(locale, item.id)}
              </h1>
              <div>
                <h2 className="text-2xl md:text-3xl font-semibold text-primary mb-4">
                  {item.heading}
                </h2>
                <ul className="list-item BenefitsPart list-inside text-lg text-body-color space-y-2 relative">
                  {item.points.map((p: string, index: number) => (
                    <li key={index}>{p}</li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>

        {/* Right Image */}
        <div>
          <img
            src={getImagePath("/images/benefits/BenefitsforEndUsers-img.png")}
            alt="Benefits for Customers"
            className="w-full h-auto mx-auto"
          />
        </div>
      </div>

      {/* Remaining Items */}
      <div className="container grid grid-cols-1 lg:grid-cols-2 gap-5 lg:gap-20 items-start justify-center mt-5">
        {items.slice(2).map((item: any) => (
          <div key={item.id} className="flex items-start gap-2">
            <h1 className="numbering text-primary">
              {formatNumberByLocale(locale, item.id)}
            </h1>
            <div>
              <h2 className="text-2xl md:text-3xl font-semibold text-primary mb-4">
                {item.heading}
              </h2>
              <ul className="list-item BenefitsPart list-inside text-lg text-body-color space-y-2">
                {item.points.map((p: string, index: number) => (
                  <li key={index}>{p}</li>
                ))}
              </ul>
            </div>
          </div>
        ))}
      </div>

      {/* Bottom Sections */}
      <div className="container mt-10 mb-24">
        <div className="flex justify-center items-center mt-10 pb-8 relative w-full">
          <div className="w-full max-w-5xl">
            <Benefits benefitsToShow={[1, 2]} />
          </div>
        </div>
      </div>

      <TopFilmsSingleSection />
      <HowItWork />
      {/* <Testimonials /> */}
    </section>
  );
}
